import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { pathgen } from 'object-path-generator';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts.ts';
export const useQueryStateTestIds = pathgen('useQueryStateTestIds');
export const useQueryState = (options)=>{
    const { queryKey, withLoader, toasts } = options;
    const { refreshOnHookMount: _refreshOnMount, toasts: _toasts } = options, restOptions = _object_without_properties(options, [
        "refreshOnHookMount",
        "toasts"
    ]);
    const queryKeyString = useMemo(()=>JSON.stringify(queryKey), [
        queryKey
    ]);
    const [previousQueryKeyString, setPreviousQueryKeyString] = useState(queryKeyString);
    const queryClient = useQueryClient();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    function getRefetchOnMount(query, refreshOnMount) {
        if (typeof refreshOnMount === 'function') {
            return refreshOnMount(query) ? 'always' : false;
        }
        return refreshOnMount ? 'always' : false;
    }
    const _useQuery = useQuery(_object_spread_props(_object_spread({
        keepPreviousData: previousQueryKeyString === queryKeyString,
        retry: false,
        refetchOnMount: (query)=>getRefetchOnMount(query, options.refreshOnHookMount)
    }, restOptions), {
        onError: (err)=>{
            var _options_onError;
            if (toasts === null || toasts === void 0 ? void 0 : toasts.errorMessage) {
                var _toasts_errorTestId;
                showErrorToast(toasts.errorMessage, (_toasts_errorTestId = toasts.errorTestId) !== null && _toasts_errorTestId !== void 0 ? _toasts_errorTestId : useQueryStateTestIds.ErrorToast());
            }
            (_options_onError = options.onError) === null || _options_onError === void 0 ? void 0 : _options_onError.call(options, err);
        },
        onSettled: ()=>{
            setPreviousQueryKeyString(queryKeyString);
        },
        networkMode: 'always'
    })), { data, isFetching, isInitialLoading, refetch, error, isLoading: _isLoading } = _useQuery, rest = _object_without_properties(_useQuery, [
        "data",
        "isFetching",
        "isInitialLoading",
        "refetch",
        "error",
        "isLoading"
    ]);
    const { setQueryAsLoading } = useStoreActions();
    const isQueryLoading = useStore((state)=>!!state.isQueryLoading[queryKeyString]);
    const isLoading = useMemo(()=>isInitialLoading || isQueryLoading, [
        isInitialLoading,
        isQueryLoading
    ]);
    useEffect(()=>{
        if (isLoading && withLoader) {
            startLoader(queryKeyString);
            return ()=>{
                stopLoader(queryKeyString);
            };
        }
    }, [
        isLoading,
        queryKeyString,
        withLoader
    ]);
    const refetchData = useCallback(async function() {
        let { silent = true, throwOnError = true } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        !silent && setQueryAsLoading(queryKeyString, true);
        try {
            return await refetch({
                throwOnError,
                cancelRefetch: false
            });
        } finally{
            !silent && setQueryAsLoading(queryKeyString, false);
        }
    }, [
        queryKeyString
    ]);
    const optimisticUpdate = (updaterOrValue)=>{
        queryClient.setQueryData(queryKey, (old)=>{
            if (!old) {
                return old;
            }
            if (typeof updaterOrValue === 'function') {
                return updaterOrValue(old);
            }
            return updaterOrValue;
        });
    };
    return _object_spread({
        data,
        optimisticUpdate,
        isFetching,
        isLoading,
        isBackgroundLoading: isFetching && !isLoading,
        refresh: refetchData,
        error
    }, rest);
};
